import { useCallback, useEffect, useState } from 'react'

import usePubNub from '@shared/hooks/src/usePubNub'
import useUserSetting from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { PubNub, UserSettingName } from '@shared/utils'

const ACTIONS = {
  JOIN: 'join',
  CURRENT_VIEWER: 'current_viewer',
  LEAVE: 'leave',
  HEARTBEAT: 'heartbeat',
}

const CHANNEL_PREFIX = 'entity_viewing'
const HEARTBEAT_INTERVAL = 30000 // ms (30 seconds)
const INACTIVITY_LIMIT = 120000 // ms (2 minutes)
const CLEANUP_INTERVAL = 60000 // ms (1 minute)

/**
 * Hook to track and manage users viewing a specific entity
 *
 * @param {string} entityType - The type of entity being viewed (e.g., 'patient', 'appointment')
 * @param {string} entityId - The ID of the entity being viewed
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether the hook is enabled
 * @returns {Array} - Array of active viewers (excluding the current user)
 */
export default function useEntityViewers(entityType, entityId, options = {}) {
  const { enabled = true } = options

  const me = useMe()
  const [viewers, setViewers] = useState([])
  const hidePresence = useUserSetting(me, UserSettingName.HidePresence)?.value ?? false

  const channel = `${CHANNEL_PREFIX}_${entityType}_${entityId}`

  const addOrUpdateViewer = useCallback((newViewer) => {
    setViewers((prev) => {
      const existingViewer = prev.find((viewer) => viewer.id === newViewer.id)
      if (existingViewer) {
        return prev.map((viewer) => (viewer.id === newViewer.id ? { ...viewer, ...newViewer } : viewer))
      }
      return [...prev, newViewer]
    })
  }, [])

  const removeViewerById = useCallback((viewerId) => {
    setViewers((prev) => prev.filter((viewer) => viewer.id !== viewerId))
  }, [])

  const handleIncomingMessage = useCallback(
    (data) => {
      const { action, attributes } = data

      switch (action) {
        case ACTIONS.JOIN:
          addOrUpdateViewer(attributes)

          // Provide existing user info to the newly joined user (if it's someone else)
          if (attributes.id !== me.id) {
            PubNub.emit(
              channel,
              JSON.stringify({
                action: ACTIONS.CURRENT_VIEWER,
                attributes: makeUserInfo(me, hidePresence),
              })
            )
          }
          break

        case ACTIONS.CURRENT_VIEWER:
          addOrUpdateViewer(attributes)
          break

        case ACTIONS.LEAVE:
          removeViewerById(attributes.id)
          break

        case ACTIONS.HEARTBEAT:
          addOrUpdateViewer({
            ...attributes,
            lastSeen: new Date().toISOString(),
          })
          break

        default:
          break
      }
    },
    [addOrUpdateViewer, me, removeViewerById, channel, hidePresence]
  )

  usePubNub(channel, handleIncomingMessage, {
    enabled: Boolean(entityId && enabled && me),
  })

  // Send join and heartbeat messages when the hook is active
  useEffect(() => {
    if (!entityId || !me || !enabled) return

    const userInfo = makeUserInfo(me, hidePresence)

    // Join
    PubNub.emit(
      channel,
      JSON.stringify({
        action: ACTIONS.JOIN,
        attributes: userInfo,
      })
    )

    // Heartbeat at a regular interval
    const heartbeatIntervalId = setInterval(() => {
      PubNub.emit(
        channel,
        JSON.stringify({
          action: ACTIONS.HEARTBEAT,
          attributes: userInfo,
        })
      )
    }, HEARTBEAT_INTERVAL)

    return () => {
      clearInterval(heartbeatIntervalId)

      // Leave
      PubNub.emit(
        channel,
        JSON.stringify({
          action: ACTIONS.LEAVE,
          attributes: { id: me.id },
        })
      )
    }
  }, [channel, entityId, me, enabled, hidePresence])

  // Periodically remove inactive viewers
  useEffect(() => {
    const cleanupIntervalId = setInterval(() => {
      const now = Date.now()
      setViewers((prev) => {
        return prev.filter((viewer) => {
          if (!viewer.lastSeen) return true
          return now - new Date(viewer.lastSeen).getTime() < INACTIVITY_LIMIT
        })
      })
    }, CLEANUP_INTERVAL)

    return () => clearInterval(cleanupIntervalId)
  }, [])

  // Exclude current and anonymous users from the returning list of viewers
  return viewers.filter((viewer) => viewer.id !== me.id).filter((viewer) => !viewer.hide)
}

function makeUserInfo(user, hidePresence = false) {
  return {
    id: user.id,
    fullName: user.fullName,
    firstName: user.firstName,
    lastName: user.lastName,
    role: user.role,
    profilePictureThumbUrl: user.profilePictureThumbUrl,
    lastSeen: new Date().toISOString(),
    hide: hidePresence,
  }
}
